import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { Container, Row, Col } from 'react-bootstrap';
import { usePage } from 'context/PageContext';
import {
    getFacility,
    getFacilityLiveAuction,
    getFacilityOnlineAuction,
    getFacilityOnlineAuctionComingSoon,
    setThingamabobActions,
} from 'actions';
import AuctionTile from 'components/Auctions/AuctionTile';
import Advertisement from './_components/Advertisement';
import { Loading } from 'components/Loading';
import MissingBlock from 'components/partials/MissingBlock';
import { decode } from 'helpers/entities';
import FacilityLiveAuctionTile from 'components/LiveAuctions/FacilityLiveAuctionTile';
import Pagination from 'components/partials/Pagination';
import { scrollTo, waitForElement } from 'helpers/page';
import Schema from 'components/Schemas/FacilityHomepageSchema';
import FacilityMap from './_components/FacilityMap';
import FacilityActions from './_components/FacilityActions';
import FacilityInfo from './_components/FacilityInfo';
import AuctionTabs from './_components/AuctionTabs';
import FacilityFeaturesCard from './_components/FacilityFeaturesCard';
import FacilityHoursCard from './_components/FacilityHoursCard';
import CharityStorageCard from './_components/CharityStorageCard';
import { Facility } from 'types/Facility';
import 'styles/components/auction-tile.less';
import 'styles/components/facility-homepage.less';
import 'styles/Auctions/OnlineSearch.less';

interface HomepageProps {
    facility_id: string
    redirect_id?: string
    active: boolean
}

interface RootState {
    facility: {
        data: {
            facility: Facility
            facility_hours: any[]
        }
        auctions: any[]
        auctions_coming_soon: any[]
        live_auctions: any[]
        loading: boolean
        loading_auctions: boolean
        loading_auctions_coming_soon: boolean
        loading_auctions_live: boolean
        auctions_total_records: number
        auctions_live_total_records: number
        auctions_coming_soon_total_records: number
    }
    user: {
        authed: boolean
        reg_type: number
    }
}

const Homepage: React.FC<HomepageProps> = ({ facility_id, redirect_id, active }) => {
    const dispatch = useDispatch();
    const router = useRouter();
    const { setPage, resetPage } = usePage();
    const advertisementRef = useRef<HTMLDivElement>(null);
    const {
        facility,
        facilityHours,
        auctions,
        auctionsComingSoon,
        auctionsLive,
        loading,
        loadingAuctions,
        loadingAuctionsComingSoon,
        loadingAuctionsLive,
        auctionsTotalRecords,
        auctionsLiveTotalRecords,
        auctionsComingSoonTotalRecords,
    } = useSelector((state: RootState) => ({
        facility: state.facility.data.facility,
        facilityHours: state.facility.data.facility_hours,
        auctions: state.facility.auctions,
        auctionsComingSoon: state.facility.auctions_coming_soon,
        auctionsLive: state.facility.live_auctions,
        loading: state.facility.loading,
        loadingAuctions: state.facility.loading_auctions,
        loadingAuctionsComingSoon: state.facility.loading_auctions_coming_soon,
        loadingAuctionsLive: state.facility.loading_auctions_live,
        auctionsTotalRecords: state.facility.auctions_total_records,
        auctionsLiveTotalRecords: state.facility.auctions_live_total_records,
        auctionsComingSoonTotalRecords: state.facility.auctions_coming_soon_total_records,
    }));
    const { authed, reg_type } = useSelector((state: RootState) => state.user);

    const [currentTab, setCurrentTab] = useState<string>('online-auctions');
    const [selectedOnlineAuctionType, setSelectedOnlineAuctionType] = useState<string>('1');
    const [auctionsPage, setAuctionsPage] = useState<number>(1);
    const [auctionsComingSoonPage, setAuctionsComingSoonPage] = useState<number>(1);
    const [auctionsLivePage, setAuctionsLivePage] = useState<number>(1);

    useEffect(() => {
        dispatch(getFacilityOnlineAuction(facility_id, 1));
        dispatch(getFacilityOnlineAuctionComingSoon(facility_id, 1));
        dispatch(getFacilityLiveAuction(facility_id, 1));

        checkUrlHash();
        handleRedirect();

        return () => {
            dispatch(setThingamabobActions());
        };
    }, [dispatch, facility_id]);

    useEffect(() => {
        setPage({
            h1: 'Self Storage Auction Facility Details',
            title: `Storage Auctions at ${facility?.facility_name}`,
            marginBottom: 'pageTitle marginB20',
            description: `Storage unit auctions are happening now at a self storage facility in ${facility?.city}. Explore active auction details and photos, then start bidding & winning!`,
            og: {
                description: `Storagetreasures.com | ${facility?.facility_name}`,
                image: undefined,
            },
        });
        
        return () => {
            resetPage();
        };
    }, [facility]);

    const handleRedirect = () => {
        if (!active && redirect_id !== undefined) {
            router.push(`/facilities/${redirect_id}`);
        } else if (!active && redirect_id === undefined) {
            router.push(`/auctions?type=zipcode&term=${facility.zipcode}&radius=25&filter_types=1&filter_types=2&filter_types=3`);
        }
    };

    const checkUrlHash = () => {
        const hash = window.location.hash;
        if (hash === '#live-auctions') {
            waitForElement('a[href="#live-auctions"]').then(({ element }) => {
                (element as HTMLElement).click();
                scrollTo(0);
            });
        }
    };

    const doAuctionSearch = (type: string, pageNum: number) => {
        switch (type) {
            case 'auctions':
                dispatch(getFacilityOnlineAuction(facility_id, pageNum));
                setAuctionsPage(pageNum);
                break;
            case 'auction-coming-soon':
                dispatch(getFacilityOnlineAuctionComingSoon(facility_id, pageNum));
                setAuctionsComingSoonPage(pageNum);
                break;
            case 'auctions_live':
                dispatch(getFacilityLiveAuction(facility_id, pageNum));
                setAuctionsLivePage(pageNum);
                break;
        }
    };

    const renderAuctionTiles = (auctionType: string = '') => {
        const loading = {
            auctions: loadingAuctions,
            auctions_coming_soon: loadingAuctionsComingSoon,
            auctions_live: loadingAuctionsLive,
        }[auctionType];

        if (loading) {
            return <Loading />;
        }

        const currentPage = {
            auctions: auctionsPage,
            auctions_coming_soon: auctionsComingSoonPage,
            auctions_live: auctionsLivePage,
        }[auctionType];

        const auctionTotalCount = {
            auctions: auctionsTotalRecords,
            auctions_coming_soon: auctionsComingSoonTotalRecords,
            auctions_live: auctionsLiveTotalRecords,
        }[auctionType];

        const auctionData = {
            auctions,
            auctions_coming_soon: auctionsComingSoon,
            auctions_live: auctionsLive,
        }[auctionType];

        return (
            <>
                <div className="d-flex justify-content-center">
                    <MissingBlock show={auctionData?.length === 0} />
                </div>
                <Col className="text-center">
                    {auctionData?.map((auction, index) => (
                        auctionType === 'auctions_live' ? (
                            <FacilityLiveAuctionTile key={index} auction={auction} />
                        ) : (
                            // @ts-ignore
                            <AuctionTile allowHorizontal key={index} auction={auction} />
                        )
                    ))}
                </Col>
                <Row>
                    <Pagination
                        perPage={10}
                        dataCount={auctionTotalCount}
                        currentPage={currentPage}
                        passThrough={auctionType}
                        onSetPage={(pageNum: number, type: string) => doAuctionSearch(type, pageNum)}
                    />
                </Row>
            </>
        );
    };

    if (loading) {
        return <Loading loading_component={[{ label: 'Loading', loading }]} />;
    }

    return (
        <Container fluid className="facility-homepage">
            <Schema facility={facility} />
            <Row>
                <Col lg={3}>
                    <FacilityInfo facility={facility} />
                    <Advertisement ref={advertisementRef} src={facility?.ad_file || ''} />
                    <FacilityHoursCard hours={facilityHours} />
                    <FacilityFeaturesCard features={facility?.features || []} />
                    {facility.charity_storage && <CharityStorageCard />}
                </Col>    
                <Col lg={9}>
                    <FacilityActions facility={facility} authed={authed} reg_type={reg_type} facility_id={facility_id} />
                    {facility?.marker && <FacilityMap marker={facility?.marker} />}
                    <AuctionTabs
                        facility={facility}
                        renderAuctionTiles={renderAuctionTiles}
                        selectedOnlineAuctionType={selectedOnlineAuctionType}
                        setSelectedOnlineAuctionType={setSelectedOnlineAuctionType}
                        currentTab={currentTab}
                        setCurrentTab={setCurrentTab}
                    />
                </Col>    
                <Col xs={12}>
                    <h2 className="facility-information">Facility Information</h2>
                    <div className="knowledge-wrapper mt-3 mb-4 p-3">
                        <p>{decode(facility?.facility_name || '')} hosts storage auctions at {facility.address}, {facility.city}, {facility.state} {facility.zipcode}. The self storage auctions we
                            host typically contain a variety of treasures including: appliances, clothing & shoes, collectibles,
                            computers, electronics, household furniture, mattresses, sporting & outdoor goods, tools and a wide variety
                            of other items. We regularly hold auctions in {facility.city}, {facility.state} so be sure to check back often to see our latest listings!</p>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

// @ts-ignore
Homepage.getInitialProps = async ({ store: { dispatch }, query: { account_id: facility_id } }: { store: { dispatch: any }, query: { account_id: string } }) => {
    const { facility, redirect_id } = await dispatch(getFacility(facility_id));

    return { ...facility, facility_id, logo_file: facility?.image, redirect_id };
};

export default Homepage;